import { getAddress } from "../api/google";
import { jwtDecode } from "jwt-decode";
import { regex } from "./constants";

/**
 * Friendly, user-facing reasons for a failed location lookup. The browser's own
 * strings ("Only secure origins are allowed (see: https://goo.gl/Y0ZkNV).",
 * "User denied Geolocation") are developer messages and must not reach a toast.
 */
export const LOCATION_ERROR = {
  UNSUPPORTED: "This browser can't share your location. Set it manually instead.",
  INSECURE_CONTEXT:
    "Location needs a secure (https) connection. Set your location manually instead.",
  PERMISSION_DENIED:
    "Location access is off. Allow it in your browser settings or set your location manually.",
  POSITION_UNAVAILABLE:
    "We couldn't determine your location right now. Try again or set it manually.",
  TIMEOUT: "Finding your location took too long. Try again or set it manually.",
};

const GEO_TIMEOUT_MS = 10000;

export async function getDeviceCoordinates() {
  try {
    if (!navigator.geolocation) {
      return { error: true, code: "UNSUPPORTED", message: LOCATION_ERROR.UNSUPPORTED };
    }
    // Chrome refuses geolocation on http origins other than localhost (the
    // local dev stack at http://myhealthvaults.frontend is one). Say so in
    // plain words instead of surfacing the browser's developer message.
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      return {
        error: true,
        code: "INSECURE_CONTEXT",
        message: LOCATION_ERROR.INSECURE_CONTEXT,
      };
    }
    const fetchDeviceLocation = await new Promise(function (resolve) {
      const success = (position) => {
        const deviceCoordinates = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        resolve({ success: true, coordinates: deviceCoordinates });
      };

      const error = (error) => {
        // GeolocationPositionError codes: 1 denied, 2 unavailable, 3 timeout.
        const code =
          error?.code === 1
            ? "PERMISSION_DENIED"
            : error?.code === 3
              ? "TIMEOUT"
              : "POSITION_UNAVAILABLE";
        console.log("getDeviceCoordinates", code, error?.message);
        resolve({ error: true, code, message: LOCATION_ERROR[code] });
      };

      const options = {
        enableHighAccuracy: true,
        timeout: GEO_TIMEOUT_MS,
      };

      navigator.geolocation.getCurrentPosition(success, error, options);
    });
    return await fetchDeviceLocation;
  } catch (err) {
    console.log(err);
    return {
      error: true,
      code: "POSITION_UNAVAILABLE",
      message: LOCATION_ERROR.POSITION_UNAVAILABLE,
    };
  }
}

export function getDistance(origin, destination) {
  const lat1 = Number(origin?.lat);
  const lon1 = Number(origin?.lng);
  const lat2 = Number(destination?.lng);
  const lon2 = Number(destination?.lat);
  var p = 0.017453292519943295; // Math.PI / 180
  var c = Math.cos;
  var a =
    0.5 -
    c((lat2 - lat1) * p) / 2 +
    (c(lat1 * p) * c(lat2 * p) * (1 - c((lon2 - lon1) * p))) / 2;

  return `${Math.ceil(12742 * Math.asin(Math.sqrt(a))) || 0} km`; // 2 * R; R = 6371 km
}

export async function getFormattedAddress(location) {
  const responseData = await getAddress(location);
  // console.log("getFormattedAddress", responseData);
  const results = responseData?.data?.results;
  const streetAddress = results?.find((result) =>
    result.types.includes("street_address")
  );
  if (streetAddress?.formatted_address) {
    const addressComponents = streetAddress.formatted_address?.split(", ");
    const name = addressComponents[0];
    const address = addressComponents?.slice(1)?.join(", ");
    return { name, address };
  }
}

export async function isValidToken(token) {
  if (token?.length) {
    const decodedToken = await jwtDecode(token);
    const valid = decodedToken?.exp > Math.floor(new Date().getTime() / 1000);
    // console.log(
    //   decodedToken?.exp < Math.floor(new Date().getTime() / 1000),
    //   decodedToken?.exp,
    //   Math.floor(new Date().getTime() / 1000)
    // );
    return valid;
  }
  // console.log("Token length 0");
  return false;
}

export function getValidLogin(loginText) {
  if (loginText?.match(regex.email)) {
    return {
      email: loginText,
    };
  } else if (loginText?.match(regex.phone)) {
    return {
      phone: loginText,
    };
  } else {
    return false;
  }
}

export async function isLocationPermissionGranted() {
  return await new Promise((resolve) => {
    navigator.permissions
      .query({ name: "geolocation" })
      .then((permissionStatus) => {
        resolve(permissionStatus.state === "granted");
      });
  });
}

export function makeQueryStringFromObject(filter, query = "") {
  Object.keys(filter).forEach((key, i) => {
    if (!filter[key]) return;
    query += `${!query.length ? "?" : "&"}${key}=${filter[key]}`;
  });
  return query;
}
